import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getType } from 'typesafe-actions';
import { useActionIsPending } from '../../../hooks';
import { makeIsPendingSelector } from '../../app/redux/selectors/loading';
import { IMPORTED_ORGANIZATION_ACTION_PREFIX, importedOrganizationActions } from '../redux/actions/creators';
import {
  approveImportedOrganization,
  rejectImportedOrganization,
} from '../redux/actions/creators/importedOrganization';
import {
  makeGetImportedOrganizationFilters,
  makeGetImportedOrganizationPageInfo,
  makeGetImportedOrganizationSorting,
  makeGetImportedOrganizations,
  makeGetTotalImportedOrganizations,
} from '../redux/selectors/importedOrganization';

const useImportedOrganizationsStore = () => {
  const dispatch = useDispatch();

  const getImportedOrganizations = React.useCallback(() => {
    dispatch(importedOrganizationActions.getImportedOrganizations());
  }, [dispatch]);

  const importedOrganizations = useSelector(makeGetImportedOrganizations());
  const totalImportedOrganizations = useSelector(makeGetTotalImportedOrganizations());
  const importedOrganizationsPending = useSelector(makeIsPendingSelector(IMPORTED_ORGANIZATION_ACTION_PREFIX));

  const pageInfo = useSelector(makeGetImportedOrganizationPageInfo());
  const sortValues = useSelector(makeGetImportedOrganizationSorting());
  const activityFilters = useSelector(makeGetImportedOrganizationFilters());

  const [isApproving, startApproving] = useActionIsPending(getType(approveImportedOrganization));
  const approveOrganization = (organizationId: string) => {
    startApproving();
    dispatch(
      importedOrganizationActions.approveImportedOrganization({ id: organizationId }, () => {
        dispatch(importedOrganizationActions.getImportedOrganizations());
      })
    );
  };

  const [isRejecting, startRejecting] = useActionIsPending(getType(rejectImportedOrganization));
  const rejectOrganization = (organizationId: string) => {
    startRejecting();
    dispatch(
      importedOrganizationActions.rejectImportedOrganization({ id: organizationId }, () =>
        dispatch(importedOrganizationActions.getImportedOrganizations())
      )
    );
  };

  return {
    totalImportedOrganizations,
    importedOrganizations,
    activityFilters,
    getImportedOrganizations,
    isPending: importedOrganizationsPending,
    pageInfo,
    sortValues,
    approveOrganization,
    approveIsPending: isApproving,
    rejectOrganization,
    rejectIsPending: isRejecting,
  };
};

export default useImportedOrganizationsStore;
